// Manufacturing projects (ERP-PLAN §5.12): /projects lists every project in the usual table (ListPage: views, columns,
// Copy / CSV) with its readiness, and /projects/<code> is the project's workspace (Workspace.tsx). A row, and old links
// (?open=<id>), go to the workspace.
import { Circle, CircleAlert, CircleCheck, FolderKanban } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { Project } from "../../../../shared/db.gen.ts";
import { projectByCode, projectPath, type Readiness, readiness, STEP_STATUS_LABELS, type StepStatus } from "../../../../shared/projects.ts";
import { dayText, options, PROJECT_STATUS_LABELS } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { useProjectIndex } from "../../lib/projects.ts";
import { Link, navigate, usePath, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { CreateDialog } from "../../ui/CreateDialog.tsx";
import { Badge, Button, cx, EmptyState } from "../../ui/controls.tsx";
import type { Column } from "../../ui/DataTable.tsx";
import { ListPage } from "../../ui/ListPage.tsx";
import { Workspace } from "./Workspace.tsx";

export const PROJECT_STATUS_TONE = { npi: "accent", active: "ok", eol: "warn", archived: "neutral" } as const;

export function ProjectsPage() {
  const path = usePath();
  const params = useSearch();
  const projects = useRows("project");
  const code = decodeURIComponent(path.replace(/^\/projects\/?/, "").split("/")[0] ?? "");
  const legacy = !code ? params.get("open") : null;
  // The list stays a step back (a row's click put ?open= on it in place), so Back returns to it
  useEffect(() => {
    const p = legacy ? projects.find((x) => x.id === legacy) : undefined;
    if (!p) return;
    navigate("/projects", { replace: true });
    navigate(projectPath(p));
  }, [legacy, projects]);
  if (code) {
    const project = projectByCode(projects, code);
    return project ? (
      <Workspace key={project.id} project={project} />
    ) : (
      <EmptyState icon={<FolderKanban className="size-8" />} title={`No project ${code}`}>
        <Link to="/projects" className="text-accent-text">
          All projects
        </Link>
      </EmptyState>
    );
  }
  return <ProjectList />;
}

type ProjectRow = { id: string; project: Project; r: Readiness | null };
const STATUS_ORDER = { active: 0, npi: 1, eol: 2, archived: 3 } as const;
const SUBTITLE = "Products HHL designs and has built, each from product to production";

function ProjectList() {
  const projects = useRows("project");
  const vendors = useRows("vendor");
  const px = useProjectIndex();
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const canCreate = can("project.create");

  // In status order (active, NPI, end of life, archived), then by name: the table keeps it within a status.
  const rows = useMemo<ProjectRow[]>(
    () =>
      projects
        .filter((p) => showArchived || p.status !== "archived")
        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name))
        .map((p) => ({ id: p.id, project: p, r: readiness(px, p.id) ?? null })),
    [projects, px, showArchived],
  );
  const archived = projects.filter((p) => p.status === "archived").length;
  const vendorName = (id: string | null) => {
    const v = vendors.find((x) => x.id === id);
    return v ? v.short_name || v.name : "";
  };
  const basics = (p: Project) => !!(p.code || vendorName(p.cm_vendor_id) || p.launch_date);
  // What's left before it's ready to build: the summary after its "—" ("3 parts without a vendor, 5 specs not released")
  const toDo = (r: Readiness | null) => (r?.summary.includes("—") ? r.summary.split("— ")[1] : "");
  const columns: Column<ProjectRow>[] = [
    {
      key: "code",
      header: "Code",
      width: "100px",
      value: (x) => x.project.code,
      cell: (x) =>
        basics(x.project) ? (
          <span className="truncate font-mono text-[12px]">{x.project.code}</span>
        ) : (
          <span className="text-[12px] text-faint">No basics yet</span>
        ),
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(180px,1.4fr)",
      value: (x) => x.project.name,
      // A link, so Ctrl/⌘ or a middle click opens it in a new tab, as the cards did
      cell: (x) => (
        <Link
          to={projectPath(x.project)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className="truncate font-medium hover:underline"
        >
          {x.project.name}
        </Link>
      ),
    },
    { key: "cm", header: "CM", width: "minmax(100px,0.6fr)", value: (x) => vendorName(x.project.cm_vendor_id) },
    {
      key: "launch",
      header: "Launch",
      width: "100px",
      value: (x) => x.project.launch_date ?? "",
      text: (x) => x.project.launch_date ?? "",
      cell: (x) => <span className="text-[12px]">{x.project.launch_date ? dayText(x.project.launch_date) : ""}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "100px",
      value: (x) => STATUS_ORDER[x.project.status],
      text: (x) => PROJECT_STATUS_LABELS[x.project.status],
      cell: (x) => <Badge tone={PROJECT_STATUS_TONE[x.project.status]}>{PROJECT_STATUS_LABELS[x.project.status]}</Badge>,
    },
    {
      key: "ready",
      header: "Ready to build",
      headerTitle: "Setup steps done; hover a segment for the step and its first gap",
      width: "170px",
      value: (x) => (x.r ? x.r.ready / Math.max(1, x.r.total) : null),
      text: (x) => (x.r ? `${x.r.ready} of ${x.r.total}` : ""),
      cell: (x) =>
        x.r && (
          <>
            <ReadinessBar r={x.r} className="w-20 shrink-0" />
            <span className="text-[12px] font-medium">
              {x.r.ready} of {x.r.total}
            </span>
          </>
        ),
    },
    {
      key: "todo",
      header: "Still to do",
      width: "minmax(200px,2fr)",
      value: (x) => toDo(x.r),
      cell: (x) => (
        <span className="truncate text-[12px] text-muted" title={toDo(x.r) || undefined}>
          {toDo(x.r)}
        </span>
      ),
    },
    num("variants", "Variants", (r) => r.numbers.variants),
    num("parts", "Parts", (r) => r.numbers.parts),
    num("boms", "BOMs", (r) => r.numbers.builds),
  ];

  // A row opens its project: ListPage's ?open=<id> is the old link, which ProjectsPage turns into the workspace.
  return (
    <>
      <ListPage
        title={
          <span title={SUBTITLE}>
            Projects
            <span className="ml-3 hidden text-[13px] font-normal text-muted 2xl:inline">{SUBTITLE}</span>
          </span>
        }
        tableName="Projects"
        rows={rows}
        columns={columns}
        defaultSort={{ key: "status" }}
        newLabel="New project"
        onNew={canCreate ? () => setCreating(true) : undefined}
        toolbar={
          archived > 0 && (
            <Button size="sm" variant={showArchived ? "secondary" : "ghost"} onClick={() => setShowArchived((s) => !s)}>
              {showArchived ? "Hide archived" : `Show ${archived} archived`}
            </Button>
          )
        }
        empty={
          rows.length ? undefined : (
            <EmptyState icon={<FolderKanban className="size-8" />} title="No projects yet">
              A project holds one product HHL makes: its Shopify products, BOMs, parts, files and production.
            </EmptyState>
          )
        }
      />
      {creating && <NewProjectDialog onClose={() => setCreating(false)} />}
    </>
  );
}

/** A count from the project's readiness numbers, right-aligned. */
function num(key: string, header: string, of: (r: Readiness) => number): Column<ProjectRow> {
  return {
    key,
    header,
    width: "80px",
    align: "right",
    value: (x) => (x.r ? of(x.r) : null),
    cell: (x) => (x.r ? <span className="font-mono text-[12px]">{of(x.r)}</span> : null),
  };
}

function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const vendors = useRows("vendor");
  const users = useRows("app_user");
  const cms = vendors
    .filter((v) => !v.archived)
    .sort((a, b) => Number(a.kind === "supplier") - Number(b.kind === "supplier") || (a.short_name || a.name).localeCompare(b.short_name || b.name));
  return (
    <CreateDialog
      title="New project"
      fields={[
        { key: "name", label: "Name", placeholder: "e.g. ProGCC 4" },
        { key: "code", label: "SKU prefix", placeholder: "e.g. PGCC4" },
        { key: "status", label: "Status", type: "select", options: options(PROJECT_STATUS_LABELS) },
        {
          key: "cm_vendor_id",
          label: "Contract manufacturer",
          type: "select",
          options: [{ value: "", label: "Not chosen yet" }, ...cms.map((v) => ({ value: v.id, label: v.short_name || v.name }))],
        },
        { key: "launch_date", label: "Target launch", placeholder: "YYYY-MM-DD" },
        {
          key: "owner_id",
          label: "Owner",
          type: "select",
          options: [
            { value: "", label: "Nobody yet" },
            ...users.filter((u) => u.active && u.role !== "receiving").map((u) => ({ value: u.id, label: u.name || u.email })),
          ],
        },
      ]}
      initial={{ status: "npi" }}
      onClose={onClose}
      onSubmit={async (v) => {
        const id = crypto.randomUUID();
        const ok = await mutate("project.create", {
          id,
          name: v.name,
          code: v.code,
          status: v.status as Project["status"],
          cm_vendor_id: v.cm_vendor_id || null,
          launch_date: v.launch_date.trim() || null,
          owner_id: v.owner_id || null,
        });
        if (ok) navigate(projectPath({ id, code: v.code.trim().toUpperCase() }));
        return ok;
      }}
    />
  );
}

/** The readiness bar: one segment per setup step, by status; hover a segment for the step and its first gap. */
export function ReadinessBar({ r, className }: { r: Readiness; className?: string }) {
  return (
    <div role="img" aria-label={`${r.ready} of ${r.total} setup steps done`} className={cx("flex h-2 gap-[2px]", className)}>
      {r.steps.map((s) => (
        <span
          key={s.key}
          title={`${s.label}: ${STEP_STATUS_LABELS[s.status]}${s.gaps.filter((g) => !g.optional).length ? `\n${s.gaps.filter((g) => !g.optional)[0].text}` : ""}`}
          className={cx("min-w-0 flex-1 first:rounded-l-[4px] last:rounded-r-[4px]", STATUS_FILL[s.status])}
        />
      ))}
    </div>
  );
}

const STATUS_FILL: Record<StepStatus, string> = { done: "bg-ok", needs_work: "bg-warn", not_started: "bg-line-strong" };

/** A step's status as an icon (always next to its label, so it's never colour alone). */
export function StatusIcon({ status, className }: { status: StepStatus; className?: string }) {
  const Icon = status === "done" ? CircleCheck : status === "needs_work" ? CircleAlert : Circle;
  return (
    <Icon
      aria-label={STEP_STATUS_LABELS[status]}
      className={cx("size-4 shrink-0", status === "done" ? "text-ok" : status === "needs_work" ? "text-warn" : "text-faint", className)}
    />
  );
}
